import React, { useEffect, useState } from 'react';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import { formatDate } from '../services/datetime';
import './Sections.css';

// Opening Balance (opening_balace_fragment.xml): record the opening cash for
// today's shift. Stored per-day and read by Shift End to compute expected cash.
const OpeningBalance: React.FC = () => {
  const { formatMoney, isOn } = usePos();
  const todayStr = new Date().toISOString().split('T')[0];
  const [saved, setSaved] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(true);
  // "C - Enable Show Opening Amount": yesterday's cash-in-hand (the last closed
  // shift's expected cash) is offered as the opening amount.
  const showYesterday = isOn('enable_opening_balance_amount_set');
  const [yesterdayCash, setYesterdayCash] = useState(0);

  // Load today's saved opening balance and the last shift's expected cash.
  useEffect(() => {
    let alive = true;
    Promise.all([db.getOpeningBalance(todayStr), db.getShifts()]).then(([s, shifts]) => {
      if (!alive) return;
      setSaved(s);
      setAmount(s ? String(s) : '');
      setEditing(s === 0);
      setYesterdayCash(shifts.slice(-1)[0]?.expectedCash ?? 0);
    });
    return () => {
      alive = false;
    };
  }, [todayStr]);

  const save = () => {
    const n = parseFloat(amount) || 0;
    db.setOpeningBalance(todayStr, n);
    setSaved(n);
    setEditing(false);
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>OPENING BALANCE</h1>
      </div>

      <div className="section-card" style={{ maxWidth: 480 }}>
        <p className="muted" style={{ marginBottom: 12 }}>{formatDate(new Date())}</p>

        {!editing ? (
          <>
            <div style={{ textAlign: 'center', margin: '18px 0' }}>
              <div className="pf-lbl">Today's Opening Balance</div>
              <div style={{ fontSize: 38, fontWeight: 800, color: 'var(--color-primary-dark)' }}>
                {formatMoney(saved)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-teal" onClick={() => { setAmount(String(saved)); setEditing(true); }}>Edit</button>
            </div>
          </>
        ) : (
          <>
            {showYesterday && yesterdayCash > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span className="muted">Yesterday's cash in hand: <b>{formatMoney(yesterdayCash)}</b></span>
                <button className="btn-grey" onClick={() => setAmount(yesterdayCash.toFixed(2))}>Use</button>
              </div>
            )}
            <label className="pf-lbl" htmlFor="ob-amt">ENTER OPENING AMOUNT</label>
            <input
              id="ob-amt"
              type="number"
              min={0}
              step="0.01"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: '100%', fontSize: 24, padding: '12px', margin: '8px 0 16px', border: '1px solid var(--color-border)', borderRadius: 8 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {saved > 0 && <button className="btn-grey" onClick={() => { setAmount(String(saved)); setEditing(false); }}>Cancel</button>}
              <button className="btn-teal" onClick={save}>{saved > 0 ? 'Update' : 'Add'}</button>
            </div>
          </>
        )}
      </div>

      <p className="muted" style={{ padding: 16, maxWidth: 480 }}>
        The opening balance is used by Shift End as the day's starting cash when computing expected cash in the drawer.
      </p>
    </div>
  );
};

export default OpeningBalance;
